
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    TextField,
    MenuItem,
    Switch,
    FormControlLabel
} from "@mui/material";
import AppHeader from "../../components/AppHeader";

const LeavePolicySettings = ({ setIsLoggedIn }) => {
    const [types, setTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editOpen, setEditOpen] = useState(false);
    const [selectedType, setSelectedType] = useState(null);

    const tenant = localStorage.getItem("tenant") || "";

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL1}/leave/types`, {
                withCredentials: true,
                headers: { "X-Tenant": tenant }
            });
            if (res.data.success) {
                setTypes(res.data.data);
            }
        } catch (err) {
            console.error("Load leave types error:", err);
        }
        setLoading(false);
    }, [tenant]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleEdit = (type) => {
        setSelectedType({ ...type });
        setEditOpen(true);
    };

    const handleSave = async () => {
        try {
            const url = selectedType.id 
                ? `${process.env.REACT_APP_API_URL1}/leave/admin/types/update`
                : `${process.env.REACT_APP_API_URL1}/leave/admin/types/create`;

            const res = await axios.post(url, selectedType, {
                withCredentials: true,
                headers: { "X-Tenant": tenant }
            });
            if (res.data.success) {
                alert(selectedType.id ? "Policy updated successfully" : "Leave type created successfully");
                setEditOpen(false);
                loadData();
            }
        } catch (err) {
            console.error("Save error:", err);
            alert("Failed to save policy");
        }
    };

    const handleAddNew = () => {
        setSelectedType({
            type_name: "",
            type_code: "",
            entitlement_days: 0,
            monthly_cap: null,
            reset_basis: "calendar",
            min_service_months: 0,
            auto_grant_on_completion: 0,
            grant_amount: 0,
            sandwich_rule: 1,
            allow_half_day: 1,
            apply_20_day_rule: 0,
            accrual_mode: "upfront",
            accrual_timing: "start_of_month",
            gender_restriction: "all",
            is_document_required: 0,
            allow_combination: 1,
            allow_backdate: 1,
            exempt_combination_types: "[]"
        });
        setEditOpen(true);
    };

    return (
        <Box>
            <AppHeader setIsLoggedIn={setIsLoggedIn} />
            <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h4" fontWeight="bold">
                        Leave Policy Configuration
                    </Typography>
                    <Button variant="contained" color="primary" onClick={handleAddNew}>
                        Add New Type
                    </Button>
                </Box>

                <Paper sx={{ p: 2 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Type Name</TableCell>
                                <TableCell>Code</TableCell>
                                <TableCell>Entitlement</TableCell>
                                <TableCell>Monthly Cap</TableCell>
                                <TableCell>Consecutive Cap</TableCell>
                                <TableCell>Reset</TableCell>
                                <TableCell>Accrual</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {types.map((t) => (
                                <TableRow key={t.id}>
                                    <TableCell fontWeight="bold">{t.type_name}</TableCell>
                                    <TableCell>{t.type_code}</TableCell>
                                    <TableCell>{t.entitlement_days || 0} days</TableCell>
                                    <TableCell>{t.monthly_cap ? `${t.monthly_cap} days` : "-"}</TableCell>
                                    <TableCell>{t.max_consecutive_days || "-"}</TableCell>
                                    <TableCell sx={{ textTransform: "capitalize" }}>{t.reset_basis}</TableCell>
                                    <TableCell>{t.accrual_mode === "monthly_prorated" ? "Monthly" : "Upfront"}</TableCell>
                                    <TableCell align="right">
                                        <Button variant="outlined" size="small" onClick={() => handleEdit(t)}>
                                            Configure
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Paper>
            </Box>

            {/* Edit/Create Policy Dialog */}
            <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>{selectedType?.id ? `Configure ${selectedType?.type_name} Policy` : "Add New Leave Type"}</DialogTitle>
                <DialogContent dividers>
                    {selectedType && (
                        <Grid container spacing={3} sx={{ mt: 1 }}>
                            {!selectedType.id && (
                                <>
                                    <Grid item xs={6}>
                                        <TextField
                                            fullWidth
                                            label="Type Name"
                                            value={selectedType.type_name}
                                            onChange={(e) => setSelectedType({ ...selectedType, type_name: e.target.value })}
                                            placeholder="e.g. Marriage Leave"
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            fullWidth
                                            label="Type Code (Short)"
                                            value={selectedType.type_code}
                                            onChange={(e) => setSelectedType({ ...selectedType, type_code: e.target.value })}
                                            placeholder="e.g. ML"
                                        />
                                    </Grid>
                                </>
                            )}
                            <Grid item xs={6}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Gender Restriction"
                                    value={selectedType.gender_restriction || "all"}
                                    onChange={(e) => setSelectedType({ ...selectedType, gender_restriction: e.target.value })}
                                >
                                    <MenuItem value="all">All Genders</MenuItem>
                                    <MenuItem value="male">Male Only</MenuItem>
                                    <MenuItem value="female">Female Only</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={!!selectedType.is_document_required}
                                            onChange={(e) => setSelectedType({ ...selectedType, is_document_required: e.target.checked ? 1 : 0 })}
                                        />
                                    }
                                    label="Document Required"
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    label="Entitlement Days (Default)"
                                    type="number"
                                    value={selectedType.entitlement_days}
                                    onChange={(e) => setSelectedType({ ...selectedType, entitlement_days: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    label="Monthly Cap (Optional)"
                                    type="number"
                                    placeholder="e.g. 2"
                                    value={selectedType.monthly_cap || ""}
                                    onChange={(e) => setSelectedType({ ...selectedType, monthly_cap: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Reset Basis"
                                    value={selectedType.reset_basis}
                                    onChange={(e) => setSelectedType({ ...selectedType, reset_basis: e.target.value })}
                                >
                                    <MenuItem value="calendar">Calendar Year (Jan 1st)</MenuItem>
                                    <MenuItem value="anniversary">Anniversary (Joined Date)</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    label="Max Consecutive Days"
                                    type="number"
                                    value={selectedType.max_consecutive_days || ""}
                                    onChange={(e) => setSelectedType({ ...selectedType, max_consecutive_days: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    label="Min Service Months"
                                    type="number"
                                    value={selectedType.min_service_months}
                                    onChange={(e) => setSelectedType({ ...selectedType, min_service_months: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={!!selectedType.auto_grant_on_completion}
                                            onChange={(e) => setSelectedType({ ...selectedType, auto_grant_on_completion: e.target.checked ? 1 : 0 })}
                                        />
                                    }
                                    label="Auto Grant Balance on Eligibility"
                                />
                            </Grid>
                            {selectedType.auto_grant_on_completion === 1 && (
                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        label="Grant Amount"
                                        type="number"
                                        value={selectedType.grant_amount}
                                        onChange={(e) => setSelectedType({ ...selectedType, grant_amount: e.target.value })}
                                    />
                                </Grid>
                            )}
                            <Grid item xs={6}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={!!selectedType.sandwich_rule}
                                            onChange={(e) => setSelectedType({ ...selectedType, sandwich_rule: e.target.checked ? 1 : 0 })}
                                        />
                                    }
                                    label="Sandwich Rule (Include Off-days & Holidays)"
                                />
                            </Grid>

                            <Grid item xs={6}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={selectedType.allow_half_day !== undefined ? !!selectedType.allow_half_day : true}
                                            onChange={(e) => setSelectedType({ ...selectedType, allow_half_day: e.target.checked ? 1 : 0 })}
                                        />
                                    }
                                    label="Allow Half Day Applications"
                                />
                            </Grid>

                            <Grid item xs={6}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={selectedType.allow_combination !== undefined ? !!selectedType.allow_combination : true}
                                            onChange={(e) => setSelectedType({ ...selectedType, allow_combination: e.target.checked ? 1 : 0 })}
                                        />
                                    }
                                    label="Allow Combination with Other Leaves"
                                />
                            </Grid>

                            <Grid item xs={6}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={selectedType.allow_backdate !== undefined ? !!selectedType.allow_backdate : true}
                                            onChange={(e) => setSelectedType({ ...selectedType, allow_backdate: e.target.checked ? 1 : 0 })}
                                        />
                                    }
                                    label="Allow Backdated Applications"
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Exempt from Combination Rules (Leave Types)"
                                    SelectProps={{
                                        multiple: true
                                    }}
                                    value={(() => {
                                        try {
                                            const val = selectedType.exempt_combination_types;
                                            if (!val) return [];
                                            if (Array.isArray(val)) return val.map(Number);
                                            if (typeof val === 'string') {
                                                if (val.trim() === '') return [];
                                                const parsed = JSON.parse(val);
                                                if (Array.isArray(parsed)) return parsed.map(Number);
                                                return [Number(parsed)];
                                            }
                                            if (typeof val === 'number') return [val];
                                            return [];
                                        } catch(e) { return []; }
                                    })()}
                                    onChange={(e) => {
                                        let val = e.target.value;
                                        if (typeof val === 'string') val = val.split(',');
                                        // Ensure we store it as a JSON string of an array
                                        setSelectedType({ ...selectedType, exempt_combination_types: JSON.stringify(val.map(Number)) });
                                    }}
                                    helperText="Select leave types that are allowed to combine with this leave type regardless of rules."
                                >
                                    {types.map((t) => (
                                        <MenuItem key={t.id} value={t.id}>
                                            {t.type_name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={6}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={!!selectedType.apply_20_day_rule}
                                            onChange={(e) => setSelectedType({ ...selectedType, apply_20_day_rule: e.target.checked ? 1 : 0 })}
                                        />
                                    }
                                    label="Apply 20-Day Working Rule (Myanmar Labor Law)"
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>Advanced Accrual & Prorating Settings</Typography>
                            </Grid>

                            <Grid item xs={6}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Accrual Mode"
                                    value={selectedType.accrual_mode || 'upfront'}
                                    onChange={(e) => setSelectedType({ ...selectedType, accrual_mode: e.target.value })}
                                >
                                    <MenuItem value="upfront">Upfront (All at once)</MenuItem>
                                    <MenuItem value="monthly_prorated">Monthly Prorated (e.g. 0.5/mo)</MenuItem>
                                </TextField>
                            </Grid>

                            {selectedType.accrual_mode === 'monthly_prorated' && (
                                <Grid item xs={6}>
                                    <TextField
                                        select
                                        fullWidth
                                        label="Monthly Accrual Timing"
                                        value={selectedType.accrual_timing || 'start_of_month'}
                                        onChange={(e) => setSelectedType({ ...selectedType, accrual_timing: e.target.value })}
                                    >
                                        <MenuItem value="start_of_month">Start of Month (Proactive)</MenuItem>
                                        <MenuItem value="end_of_month">End of Month (Earned)</MenuItem>
                                    </TextField>
                                </Grid>
                            )}

                            <Grid item xs={6}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={!!selectedType.prorate_first_eligibility_year}
                                            onChange={(e) => setSelectedType({ ...selectedType, prorate_first_eligibility_year: e.target.checked ? 1 : 0 })}
                                        />
                                    }
                                    label="Prorate Stub Year (Post-Probation)"
                                />
                            </Grid>

                            <Grid item xs={6}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={!!selectedType.prorate_on_cessation}
                                            onChange={(e) => setSelectedType({ ...selectedType, prorate_on_cessation: e.target.checked ? 1 : 0 })}
                                        />
                                    }
                                    label="Prorate on Cessation (Resignations)"
                                />
                            </Grid>

                            <Grid item xs={6}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={!!selectedType.accrue_during_probation}
                                            onChange={(e) => setSelectedType({ ...selectedType, accrue_during_probation: e.target.checked ? 1 : 0 })}
                                        />
                                    }
                                    label="Accrue During Probation (Backfill)"
                                />
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave}>Save Policy</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default LeavePolicySettings;
